import { useCallback, useMemo } from 'react';
import { useLang } from 'hooks/lang';

import {
  actions,
  projectInvoiceConfigByCode,
  projectInvoiceStatusConfig,
  FullProjectInvoice,
  isOwner,
} from 'entities';

import { DropdownMenu, DropdownMenuItem } from 'components/Blocks';
import { CircleXIcon, PercentTicketIcon } from 'components/Icons';

import { DiscountRequestModal } from './DiscountRequest';

import { useToggleV2, useUser } from 'hooks';
import { useJustification } from 'providers/Justification';
import { useConfirmation } from 'providers/Confirmation';
import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';

interface ProjectInvoiceMenuProps {
  children?: never;
  projectInvoice: FullProjectInvoice;
}

export function ProjectInvoiceMenu({ projectInvoice }: ProjectInvoiceMenuProps) {
  const { t } = useLang();
  const user = useUser();

  const { project, updateProject } = useProjectProfile();

  const { requestConfirmation } = useConfirmation();
  const { openJustificationModal } = useJustification();

  const [discountModalOpen, discountModalHandlers] = useToggleV2();

  const cancelInvoice = actions.invoice.cancelProjectInvoice.use({
    onSuccess: () => {
      updateProject((old) => ({
        ...old,

        invoice: {
          ...old.invoice,

          projectInvoices: old.invoice.projectInvoices.map((invoice) =>
            invoice.id !== projectInvoice.id
              ? invoice
              : { ...projectInvoice, status: projectInvoiceStatusConfig.canceled.code },
          ),
        },
      }));
    },
  });

  const cancel = useCallback(() => {
    openJustificationModal({
      text: t('please-provide-a-reason-to-this-cancelation'),

      type: 'normal',

      onConfirm: async (justification) =>
        requestConfirmation({
          title: t('cancel-project-invoice-?') as string,
          size: 'small',
          type: 'irreversible',
          text: t('this-cancelation-will-be-final'),
          onConfirm: async () => {
            await cancelInvoice.execute({
              project: projectInvoice.project,
              projectInvoice: projectInvoice.id,
              justification,
            });
          },
        }),
    });
  }, [projectInvoice, requestConfirmation, t, openJustificationModal, cancelInvoice]);

  const menuOptions = useMemo(() => {
    return [
      {
        label: t('cancel') as string,

        icon: <CircleXIcon />,

        id: `${projectInvoice.id}-cancel`,

        onClick: cancel,

        render: [
          projectInvoiceConfigByCode[projectInvoice.status].canCancel,
          isOwner(project.invoice, user),
          user.team.isInvoiceTeam,
        ].every(Boolean),
      },
      {
        label: t('request-discount') as string,

        icon: (
          <>
            <PercentTicketIcon />
          </>
        ),

        id: `${projectInvoice.id}-discount`,

        onClick: !discountModalOpen ? discountModalHandlers.turnOn : () => null,

        render: [
          projectInvoiceConfigByCode[projectInvoice.status].canRequestDiscount,
          isOwner(project.invoice, user),
          user.team.isInvoiceTeam,
        ].every(Boolean),
      },
    ].filter(({ render }) => render);
  }, [projectInvoice, t, cancel, user, project.invoice, discountModalOpen, discountModalHandlers]);

  if (!menuOptions.length) return null;

  return (
    <>
      <DiscountRequestModal
        open={discountModalOpen}
        close={discountModalHandlers.turnOff}
        projectInvoice={projectInvoice}
      />

      <DropdownMenu tooltipContent="Options" align="end">
        {menuOptions.map(({ icon, id, label, onClick }) => (
          <DropdownMenuItem onClick={onClick} key={id}>
            {icon}
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenu>
    </>
  );
}
